import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { Tab, TabContainer } from "@saleor/components/Tab";
import {
  IVoucherLimitActions,
  IVoucherLimitState
} from "@saleor/discounts/hooks/useVoucherLimitsData";
import {
  useVoucherImageCreateMutation,
  useVoucherImageDeleteMutation
} from "@saleor/discounts/mutations";
import { RequirementsPicker } from "@saleor/discounts/types";
import {
  VoucherBrandGroupsDetail_voucher,
  VoucherBrandGroupsDetail_voucher_voucherTenants
} from "@saleor/discounts/types/VoucherBrandGroupsDetail";
import { VoucherBulkCreateVariables } from "@saleor/discounts/types/VoucherBulkCreate";
import { VoucherCustomerDetails_voucher_eligibleCustomers } from "@saleor/discounts/types/VoucherCustomerDetails";
import { VoucherImageCreateVariables } from "@saleor/discounts/types/VoucherImageCreate";
import { VoucherImageDeleteVariables } from "@saleor/discounts/types/VoucherImageDelete";
import { VoucherProductDetails_voucher_products } from "@saleor/discounts/types/VoucherProductDetails";
import { VoucherVariantDetails_voucher_productVariants } from "@saleor/discounts/types/VoucherVariantDetails";
import {
  CustomerListProps,
  ProductListProps
} from "@saleor/discounts/views/VoucherDetails/utils";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import { hasFullAccess } from "@saleor/utils/smop/checkSuperAdmin";
import { dateTimePhTimeZone } from "@saleor/utils/smop/timeZonePH";
import {
  hasNoError,
  runVoucherValidation
} from "@saleor/utils/validators/voucher";
import React, { Dispatch, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { maybe } from "../../../misc";
import { ListProps, TabListActions } from "../../../types";
import {
  DiscountDeliveryTypeEnum,
  DiscountPaymentMethodEnum,
  DiscountStatusEnum,
  DiscountValueTypeEnum,
  VoucherClassificationEnum,
  VoucherDiscountType,
  VoucherTypeEnum
} from "../../../types/globalTypes";
import { VoucherDetails_voucher } from "../../types/VoucherDetails";
import DiscountDates from "../DiscountDates";
import VoucherCustomers from "../VoucherCustomers";
import VoucherDeliveryType from "../VoucherDeliveryType";
import VoucherDescription from "../VoucherDescription";
import VoucherFreeShippingValue from "../VoucherFreeShippingValue";
import VoucherImage from "../VoucherImage";
import VoucherInfo from "../VoucherInfo";
import VoucherLimits from "../VoucherLimits";
import VoucherMallList from "../VoucherMallList";
import DiscountProducts from "../VoucherProducts";
import VoucherRequirements from "../VoucherRequirements";
import VoucherStore from "../VoucherStore";
import VoucherSummary from "../VoucherSummary";
import VoucherTypes from "../VoucherTypes";
import VoucherValue from "../VoucherValue";
import VoucherVerticals from "../VoucherVerticals";
export enum VoucherDetailsPageTab {
  categories = "categories",
  customers = "customers",
  collections = "collections",
  products = "products"
}

export function voucherDetailsPageTab(tab: string): VoucherDetailsPageTab {
  return tab === VoucherDetailsPageTab.categories
    ? VoucherDetailsPageTab.categories
    : tab === VoucherDetailsPageTab.collections
    ? VoucherDetailsPageTab.collections
    : tab === VoucherDetailsPageTab.customers
    ? VoucherDetailsPageTab.customers
    : VoucherDetailsPageTab.products;
}

export interface VoucherDetailsPageFormData {
  applyToNewCustomer: boolean;
  applyOncePerCustomer: boolean;
  applyOncePerOrder: boolean;
  bulkCreate: boolean;
  code: string;
  deliveryType: DiscountDeliveryTypeEnum[];
  description: string;
  discountType: DiscountValueTypeEnum;
  image: string;
  isAppliedOnEligibleItems: boolean;
  isEligibleItems: boolean;
  isEligibleStores: boolean;
  isAppliedToAllStores: boolean;
  isSpecificCustomer: boolean;
  isMinEligibleOnly: boolean;
  isHidden: boolean;
  tenantDiscountShare: number;
  smopDiscountShare: number;
  endDate: string;
  endTime: string;
  isAppliedToAllMalls: boolean;
  hasEndDate: boolean;
  hasUsageLimit: boolean;
  maxDiscountValue: number;
  minSpent: string;
  minCheckoutItemsQuantity: string;
  hasMinTenants: boolean;
  minTenants: number;
  paymentType: DiscountPaymentMethodEnum;
  requirementsPicker: RequirementsPicker;
  startDate: string;
  startTime: string;
  type: VoucherTypeEnum;
  usageLimit: string;
  value: number;
  verticalType: VoucherClassificationEnum;
  multiplier: number;
}

export interface VoucherDetailsPageProps
  extends Pick<ListProps, Exclude<keyof ListProps, "onRowClick">>,
    TabListActions<"productListToolbar">,
    ProductListProps,
    TabListActions<"customerListToolbar">,
    CustomerListProps {
  activeTab: VoucherDetailsPageTab;
  assignedProducts: VoucherProductDetails_voucher_products;
  assignedVariants: VoucherVariantDetails_voucher_productVariants;
  assignedTenants: VoucherBrandGroupsDetail_voucher;
  defaultCurrency: string;
  eligibleCustomers: VoucherCustomerDetails_voucher_eligibleCustomers;
  errors: DiscountErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;
  voucher: VoucherDetails_voucher;
  voucherBulkCreate: VoucherBulkCreateVariables;
  isBulkVoucher: string;
  customerPageInfo?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  productPageInfo?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  onBack: () => void;
  onFetch: (query: string) => void;
  setStoreInfo: (
    value: VoucherBrandGroupsDetail_voucher_voucherTenants
  ) => void;
  onMallAssign: () => void;
  onMallUnassign: (id: string) => void;
  onProductAssign: () => void;
  onStoreAssign: () => void;
  onProductUnassign: (id: string) => void;
  onProductClick: (id: string) => () => void;
  onRemove: () => void;
  onSubmit: (data: VoucherDetailsPageFormData) => void;
  onTabClick: (index: VoucherDetailsPageTab) => void;
  onCustomerAssign: () => void;
  onCustomerUnassign: (id: string) => void;
  onStoreUnassign: (id: string) => void;
  onStoreEdit: () => void;
  voucherLimitState: IVoucherLimitState;
  voucherLimitDispatcher: Dispatch<IVoucherLimitActions>;
}
const ProductsTab = Tab(VoucherDetailsPageTab.products);
const CustomersTab = Tab(VoucherDetailsPageTab.customers);

const VoucherDetailsPage: React.FC<VoucherDetailsPageProps> = ({
  activeTab,
  assignedProducts,
  assignedVariants,
  assignedTenants,
  customerPageInfo,
  defaultCurrency,
  disabled,
  eligibleCustomers,
  onFetch,
  errors,
  productPageInfo,
  saveButtonBarState,
  voucher,
  voucherBulkCreate,
  isBulkVoucher,
  onNextCustomerPage,
  onNextProductPage,
  onPreviousCustomerPage,
  onPreviousProductPage,
  onBack,
  onMallAssign,
  onMallUnassign,
  setStoreInfo,
  onStoreAssign,
  onStoreUnassign,
  onStoreEdit,
  onProductAssign,
  onProductClick,
  onProductUnassign,
  onTabClick,
  onRemove,
  onSubmit,
  toggle,
  toggleAll,
  selected,
  isChecked,
  productListToolbar,
  customerListToolbar,
  onCustomerAssign,
  onCustomerUnassign,
  voucherLimitState,
  voucherLimitDispatcher
}) => {
  const intl = useIntl();
  const notify = useNotifier();

  useEffect(() => {
    if (voucher) {
      onTabClick(VoucherDetailsPageTab.customers);
    }
  }, [voucher]);

  let requirementsPickerInitValue;
  if (maybe(() => voucher.minSpent.amount) > 0) {
    requirementsPickerInitValue = RequirementsPicker.ORDER;
  } else if (maybe(() => voucher.minCheckoutItemsQuantity) > 0) {
    requirementsPickerInitValue = RequirementsPicker.ITEM;
  } else {
    requirementsPickerInitValue = RequirementsPicker.NONE;
  }
  const allMalls = voucher?.malls?.edges?.map(edge => edge?.node);

  const initialForm: VoucherDetailsPageFormData = {
    applyOncePerCustomer: maybe(() => voucher.applyOncePerCustomer, false),
    applyOncePerOrder: maybe(() => voucher.applyOncePerOrder, false),
    applyToNewCustomer: maybe(() => voucher.isLimitToNewCustomer, false),
    bulkCreate: false,
    code: maybe(() => voucher.code, ""),
    deliveryType: maybe(() => voucher.deliveryTypes, []),
    description: maybe(() => voucher.description, ""),
    discountType: maybe(
      () => voucher.discountValueType,
      DiscountValueTypeEnum.FIXED
    ),
    endDate: dateTimePhTimeZone(maybe(() => voucher.endDate, "")).date,
    endTime: dateTimePhTimeZone(maybe(() => voucher.endDate, "")).time,
    hasEndDate: maybe(() => !!voucher.endDate),
    hasMinTenants: maybe(() => voucher.minTenants !== 0 || null, false),
    hasUsageLimit: maybe(() => !!voucher.usageLimit, false),
    image: voucher && voucher.imageUrl ? voucher.imageUrl.url : null,
    isAppliedOnEligibleItems: maybe(
      () => voucher.isAppliedOnEligibleItems,
      false
    ),
    isAppliedToAllMalls: !allMalls?.length ? true : false,
    isAppliedToAllStores: true,
    isEligibleItems: false,
    isEligibleStores: false,
    isHidden: maybe(() => voucher.isHidden),
    isMinEligibleOnly: maybe(() => voucher.isMinEligibleOnly, false),
    isSpecificCustomer: maybe(() => voucher.isSpecificCustomer, false),
    maxDiscountValue: maybe(() => voucher.maxDiscountValue, 0),
    minCheckoutItemsQuantity: maybe(
      () => voucher.minCheckoutItemsQuantity.toString(),
      "0"
    ),
    minSpent: maybe(() => voucher.minSpent.amount.toString(), "0"),
    minTenants: maybe(() => voucher.minTenants),
    multiplier: maybe(() => voucherBulkCreate.multiplier, 0),
    paymentType: maybe(
      () => voucher.paymentMethod,
      DiscountPaymentMethodEnum.ALL
    ),
    requirementsPicker: requirementsPickerInitValue,
    smopDiscountShare: maybe(() => voucher?.smopDiscountShare, 0),
    startDate: dateTimePhTimeZone(maybe(() => voucher.startDate, "")).date,
    startTime: dateTimePhTimeZone(maybe(() => voucher.startDate, "")).time,
    tenantDiscountShare: maybe(() => voucher?.tenantDiscountShare, 0),
    type: maybe(() => voucher.type, VoucherTypeEnum.ENTIRE_ORDER),
    usageLimit: maybe(() => voucher.usageLimit.toString(), "0"),
    value: maybe(() => voucher.discountValue, 0),
    verticalType: maybe(
      () => voucher.verticalType,
      VoucherClassificationEnum.ALL
    )
  };

  const initialValidatedForm = {
    code: null,
    deliveryType: null,
    description: null,
    distribution: null,
    endDate: null,
    minCheckoutItemsQuantity: null,
    minSpent: null,
    smopDiscountShare: null,
    tenantDiscountShare: null,
    usageLimit: null,
    value: null
  };
  const [clientErrors, setErrorValdiation] = useState(initialValidatedForm);
  const [newImageLink, setNewImageLink] = React.useState(null);

  const hasAccess = hasFullAccess();

  const voucherStatus =
    voucher?.status === DiscountStatusEnum.ACTIVE ? true : false;
  const voucherInactive =
    voucher?.status === DiscountStatusEnum.INACTIVE ? true : false;
  const voucherItemsEligible =
    maybe(() => assignedVariants.totalCount, 0) > 0 ? true : false;
  const voucherStoresEligible =
    maybe(() => assignedTenants?.voucherTenants?.length, 0) > 0 ? true : false;

  const cannotEdit = !voucherStatus && hasAccess ? false : true;
  const isBulkVoucherBoolean = isBulkVoucher ? true : false;
  const [
    createVoucherImage,
    createVoucherImageOpts
  ] = useVoucherImageCreateMutation({
    onCompleted: data => {
      const imageError = data.voucherImageCreate.errors.find(
        error => error.field === ("image" as keyof VoucherImageCreateVariables)
      );
      if (imageError) {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      } else {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Voucher Successfully Updated."
          })
        });
        setNewImageLink(data.voucherImageCreate.voucher.imageUrl.url);
      }
    }
  });

  const [
    deleteVoucherImage,
    deleteVoucherImageOpts
  ] = useVoucherImageDeleteMutation({
    onCompleted: data => {
      const imageError = data.voucherImageDelete.errors.find(
        error => error.field === ("image" as keyof VoucherImageDeleteVariables)
      );
      if (imageError) {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      } else {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Voucher Successfully Updated."
          })
        });
        setNewImageLink(null);
      }
    }
  });

  const handleSubmit = event => {
    const validate = runVoucherValidation(
      event,
      voucher?.maxDiscountValue,
      voucherLimitState
    );

    if (hasNoError(validate)) {
      setErrorValdiation(validate);
      onSubmit(event);
    } else {
      if (validate.distribution) {
        notify({
          status: "error",
          text: intl.formatMessage(
            {
              defaultMessage: "{message}"
            },
            {
              message: validate.distribution
            }
          )
        });
      }

      setErrorValdiation(validate);
    }
  };
  const labelButton = {
    delete: "Deactivate"
  };

  function getQuantity() {
    return maybe(() => assignedVariants.totalCount.toString(), "...");
  }

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ change, data, hasChanged, submit }) => {
        const breadcrumbDirectory = [
          {
            name: "Vouchers",
            url: "/discounts/vouchers"
          },
          {
            name: isBulkVoucher ? voucher.group.code : voucher?.code
          }
        ];

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader title={maybe(() => voucher.code)} />
            </div>
            <Grid>
              <div>
                <VoucherInfo
                  isBulkVoucher={isBulkVoucherBoolean}
                  data={data}
                  disabled={cannotEdit || disabled}
                  errors={errors}
                  clientErrors={clientErrors}
                  onChange={change}
                  variant="update"
                />
                <CardSpacer />
                <VoucherTypes
                  data={data}
                  disabled={true}
                  errors={errors}
                  onChange={change}
                />
                <CardSpacer />
                {data.discountType.toString() !== "SHIPPING" &&
                data.type.toString() !== "SHIPPING" &&
                data.type.toString() !== "E_VOUCHER" ? (
                  <VoucherValue
                    data={data}
                    disabled={voucherStatus || disabled}
                    defaultCurrency={defaultCurrency}
                    errors={errors}
                    clientErrors={clientErrors}
                    voucherMaxDiscountValue={voucher?.maxDiscountValue}
                    onChange={change}
                    variant="edit"
                  />
                ) : (
                  <>
                    <CardSpacer />
                    <VoucherFreeShippingValue
                      data={data}
                      disabled={disabled}
                      onChange={change}
                    />
                  </>
                )}
                <CardSpacer />
                {data.type === VoucherTypeEnum.E_VOUCHER && (
                  <>
                    <VoucherImage
                      data={data}
                      loading={
                        createVoucherImageOpts.loading ||
                        deleteVoucherImageOpts.loading
                      }
                      onDelete={() =>
                        deleteVoucherImage({
                          variables: { voucher: voucher.id }
                        })
                      }
                      onChange={change}
                      onImageUpload={file =>
                        createVoucherImage({
                          variables: {
                            image: file,
                            voucher: voucher.id
                          }
                        })
                      }
                      uploadedLink={newImageLink}
                    />
                    <CardSpacer />
                  </>
                )}
                <VoucherMallList
                  disabled={disabled}
                  malls={maybe(() => voucher.malls)}
                  emptyText="Assigned to all malls"
                  title="Malls"
                  onMallAssign={onMallAssign}
                  onMallUnassign={onMallUnassign}
                />
                <CardSpacer />
                {/* (data.type === VoucherTypeEnum.SPECIFIC_PRODUCT ||
                  data.type === VoucherTypeEnum.E_VOUCHER) &&*/
                data.discountType.toString() !== "SHIPPING" ? (
                  <>
                    <TabContainer>
                      <CustomersTab
                        isActive={activeTab === VoucherDetailsPageTab.customers}
                        changeTab={onTabClick}
                      >
                        {intl.formatMessage(
                          {
                            defaultMessage: "Customers ({quantity})",
                            description: "number of products"
                          },
                          {
                            quantity: maybe(() => eligibleCustomers.totalCount)
                          }
                        )}
                      </CustomersTab>
                      {(data.type === VoucherTypeEnum.SPECIFIC_PRODUCT ||
                        data.type === VoucherTypeEnum.E_VOUCHER) && (
                        <ProductsTab
                          isActive={
                            activeTab === VoucherDetailsPageTab.products
                          }
                          changeTab={onTabClick}
                        >
                          {intl.formatMessage(
                            {
                              defaultMessage: "Products",
                              description: "number of products"
                            },
                            {
                              quantity: getQuantity()
                            }
                          )}
                        </ProductsTab>
                      )}
                      {/* </TabContainer>
                    <TabContainer>
                      {data.type === VoucherTypeEnum.SPECIFIC_CUSTOMER ? (
                        <CustomersTab
                          isActive={activeTab === VoucherDetailsPageTab.customers}
                          changeTab={onTabClick}
                        >
                          {intl.formatMessage(
                            {
                              defaultMessage: "Customers ({quantity})",
                              description: "number of products"
                            },
                            {
                              quantity: maybe(() => eligibleCustomers.totalCount)
                            }
                          )}
                        </CustomersTab>
                      ) : (
                        <ProductsTab
                          isActive={activeTab === VoucherDetailsPageTab.products}
                          changeTab={onTabClick}
                        >
                          {intl.formatMessage(
                            {
                              defaultMessage: "Products ({quantity})",
                              description: "number of products"
                            },
                            {
                              quantity: getQuantity()
                            }
                          )}
                        </ProductsTab>
                      )} */}
                      {/* <CategoriesTab
                        isActive={activeTab === VoucherDetailsPageTab.categories}
                        changeTab={onTabClick}
                      >
                        {intl.formatMessage(
                          {
                            defaultMessage: "Categories ({quantity})",
                            description: "number of categories"
                          },
                          {
                            quantity: maybe(
                              () => voucher.categories.totalCount.toString(),
                              "…"
                            )
                          }
                        )}
                      </CategoriesTab> */}
                      {/* <CollectionsTab
                        isActive={activeTab === VoucherDetailsPageTab.collections}
                        changeTab={onTabClick}
                      >
                        {intl.formatMessage(
                          {
                            defaultMessage: "Collections ({quantity})",
                            description: "number of collections"
                          },
                          {
                            quantity: maybe(
                              () => voucher.collections.totalCount.toString(),
                              "…"
                            )
                          }
                        )}
                      </CollectionsTab> */}
                    </TabContainer>
                    <CardSpacer />
                    {activeTab === VoucherDetailsPageTab.categories ? (
                      <></>
                    ) : // <DiscountCategories
                    //   disabled={voucherStatus || disabled}
                    //   onCategoryAssign={onCategoryAssign}
                    //   onCategoryUnassign={onCategoryUnassign}
                    //   onNextPage={onNextPage}
                    //   onPreviousPage={onPreviousPage}
                    //   onRowClick={onCategoryClick}
                    //   pageInfo={pageInfo}
                    //   discount={voucher}
                    //   isChecked={isChecked}
                    //   selected={selected}
                    //   toggle={toggle}
                    //   toggleAll={toggleAll}
                    //   toolbar={categoryListToolbar}
                    // />
                    activeTab === VoucherDetailsPageTab.collections ? (
                      <></>
                    ) : activeTab === VoucherDetailsPageTab.customers ? (
                      <VoucherCustomers
                        data={data}
                        disabled={voucherStatus || disabled}
                        onChange={change}
                        onNextPage={() => undefined}
                        onPreviousPage={() => undefined}
                        onNextCustomerPage={onNextCustomerPage}
                        onPreviousCustomerPage={onPreviousCustomerPage}
                        onCustomerAssign={onCustomerAssign}
                        onCustomerUnassign={onCustomerUnassign}
                        onRowClick={onProductClick}
                        customerPageInfo={customerPageInfo}
                        pageInfo={productPageInfo}
                        voucher={voucher}
                        isChecked={isChecked}
                        selected={selected}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        toolbar={customerListToolbar}
                        voucherCustomers={maybe(() => eligibleCustomers.edges)}
                      />
                    ) : (
                      // <DiscountCollections
                      //   disabled={voucherStatus || disabled}
                      //   onCollectionAssign={onCollectionAssign}
                      //   onCollectionUnassign={onCollectionUnassign}
                      //   onNextPage={onNextPage}
                      //   onPreviousPage={onPreviousPage}
                      //   onRowClick={onCollectionClick}
                      //   pageInfo={pageInfo}
                      //   discount={voucher}
                      //   isChecked={isChecked}
                      //   selected={selected}
                      //   toggle={toggle}
                      //   toggleAll={toggleAll}
                      //   toolbar={collectionListToolbar}
                      // />
                      <>
                        <DiscountProducts
                          disabled={voucherStatus || voucherStoresEligible}
                          voucherStatus={voucherStatus}
                          onNextPage={() => undefined}
                          onPreviousPage={() => undefined}
                          onNextProductPage={onNextProductPage}
                          onPreviousProductPage={onPreviousProductPage}
                          onProductAssign={onProductAssign}
                          onProductUnassign={onProductUnassign}
                          onRowClick={onProductClick}
                          productPageInfo={productPageInfo}
                          pageInfo={productPageInfo}
                          voucher={voucher}
                          isChecked={isChecked}
                          selected={selected}
                          toggle={toggle}
                          toggleAll={toggleAll}
                          toolbar={productListToolbar}
                          voucherProducts={maybe(() => assignedProducts.edges)}
                          voucherVariants={maybe(() => assignedVariants.edges)}
                          data={data}
                          onChange={change}
                          elligibleProducts={assignedVariants}
                          onFetch={onFetch}
                        />
                        <CardSpacer />
                        <CardSpacer />
                        <VoucherStore
                          setStoreInfo={setStoreInfo}
                          disabled={disabled || voucherItemsEligible}
                          voucherStatus={voucherStatus}
                          onStoreAssign={onStoreAssign}
                          onStoreUnassign={onStoreUnassign}
                          onStoreEdit={onStoreEdit}
                          voucher={voucher}
                          data={data}
                          stores={assignedTenants}
                          onChange={change}
                        />
                      </>
                    )}
                  </>
                ) : null}
                <CardSpacer />
                {/* {data.discountType.toString() === "SHIPPING" ||
                data.type.toString() === "SHIPPING" ? (
                  <>
                    <TextField
                      disabled={true}
                      fullWidth
                      name={
                        "isAppliedToAllMalls" as keyof VoucherDetailsPageFormData
                      }
                      type="hidden"
                      value={data.isAppliedToAllMalls}
                      onChange={change}
                    />
                    {!hideVoucherMall && (
                      <VoucherMallList
                        malls={allMalls}
                        disabled={voucherStatus || disabled}
                        emptyText={intl.formatMessage({
                          defaultMessage: "Voucher applies to all malls"
                        })}
                        title={
                          <>
                            {intl.formatMessage({
                              defaultMessage: "Malls",
                              description: "voucher mall range"
                            })}
                            <Typography variant="caption">
                              <FormattedMessage defaultMessage="Voucher is limited to these malls" />
                            </Typography>
                          </>
                        }
                        onMallAssign={onMallAssign}
                        onMallUnassign={onMallUnassign}
                      />
                    )}
                  </>
                ) : null} */}
                <CardSpacer />
                <VoucherDescription
                  data={data}
                  errors={errors}
                  clientErrors={clientErrors}
                  disabled={voucherStatus || disabled}
                  onChange={change}
                  variant="create"
                />
                <CardSpacer />
                <VoucherVerticals
                  data={data}
                  disabled={voucherStatus || disabled}
                  errors={errors}
                  onChange={change}
                />
                {data.type !== VoucherDiscountType.E_VOUCHER && (
                  <>
                    <CardSpacer />
                    <VoucherDeliveryType
                      data={data}
                      disabled={voucherStatus || disabled}
                      errors={errors}
                      clientErrors={clientErrors}
                      onChange={change}
                    />
                    {/* <CardSpacer />
                      <VoucherPaymentType
                        data={data}
                        disabled={disabled}
                        errors={errors}
                        onChange={change}
                      /> */}
                  </>
                )}
                <CardSpacer />
                <VoucherRequirements
                  data={data}
                  disabled={
                    voucherStatus || disabled || data.type === "E_VOUCHER"
                  }
                  defaultCurrency={defaultCurrency}
                  errors={errors}
                  clientErrors={clientErrors}
                  onChange={change}
                />
                <CardSpacer />
                <VoucherLimits
                  data={data}
                  disabled={voucherStatus || disabled}
                  defaultCurrency={defaultCurrency}
                  errors={errors}
                  clientErrors={clientErrors}
                  onChange={change}
                  voucherLimitState={voucherLimitState}
                  voucherLimitDispatcher={voucherLimitDispatcher}
                />
                <CardSpacer />
                <DiscountDates
                  data={data}
                  disabled={disabled}
                  defaultCurrency={defaultCurrency}
                  clientErrors={clientErrors}
                  errors={errors}
                  onChange={change}
                />
              </div>
              <div>
                <VoucherSummary
                  defaultCurrency={defaultCurrency}
                  voucher={voucher}
                />
              </div>
            </Grid>
            <SaveButtonBar
              id="voucher"
              disabled={
                disabled || !(hasChanged || voucherLimitState.hasBeenModified)
              }
              onCancel={onBack}
              onDelete={
                voucherInactive ? null : voucherStatus ? null : onRemove
              }
              labels={labelButton}
              onSave={submit}
              state={saveButtonBarState}
            />
          </Container>
        );
      }}
    </Form>
  );
};
VoucherDetailsPage.displayName = "VoucherDetailsPage";

export default VoucherDetailsPage;
